/**
 * ============================================================================
 * DAY VIEW TIMELINE - HORIZONTAL TIMELINE WITH COURTS
 * ============================================================================
 *
 * Displays a horizontal timeline with:
 * - Courts/Fields on the left side (grouped by parent-child)
 * - Hourly timeline on the right (scrollable)
 * - Bookings as colored blocks spanning their time range
 * - Current time indicator (red line)
 * - Click on booking to see details
 *
 * ============================================================================
 */

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { X } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface Court {
  id: number;
  name: string;
  sport_id: number;
  sport?: { id: number; name: string };
  parent_court_id?: number | null;
  is_active?: boolean;
}

interface Booking {
  id: number;
  court_id: number;
  booking_reference?: string;
  start_time: string;
  end_time: string;
  customer_name?: string;
  customer?: string;
  sport_name?: string;
  type: 'spotts' | 'manual' | 'recurring' | 'blocked' | 'cancelled' | 'external';
  status?: string;
  rawData?: any;
}

interface OperatingHour {
  day_of_week: string;
  opening_time: string;
  closing_time: string;
  is_closed: boolean;
}

interface DayViewTimelineProps {
  selectedDate: Date;
  courts: Court[];
  operatingHours?: OperatingHour[];
  bookings: Booking[];
  onBookingClick?: (booking: Booking) => void;
  onEmptySlotClick?: (courtId: number, time: string) => void;
  isLoading?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse time string to hours (decimal)
 */
function parseTimeToHours(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours + minutes / 60;
}

/**
 * Format time for display (handles hours >= 24 for overnight)
 */
function formatTime(hour: number): string {
  const h = Math.floor(hour >= 24 ? hour - 24 : hour);
  const m = Math.round((hour - Math.floor(hour)) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

/**
 * Get booking type color classes
 */
function getBookingColors(type: string): { bg: string; border: string; text: string } {
  switch (type) {
    case 'spotts':
      return { bg: 'bg-blue-100', border: 'border-l-blue-500', text: 'text-blue-900' };
    case 'manual':
      return { bg: 'bg-green-100', border: 'border-l-green-500', text: 'text-green-900' };
    case 'recurring':
      return { bg: 'bg-purple-100', border: 'border-l-purple-500', text: 'text-purple-900' };
    case 'blocked':
      return { bg: 'bg-orange-100', border: 'border-l-orange-500', text: 'text-orange-900' };
    case 'external':
      return { bg: 'bg-teal-100', border: 'border-l-teal-500', text: 'text-teal-900' };
    case 'cancelled':
      return { bg: 'bg-red-100', border: 'border-l-red-500', text: 'text-red-900 opacity-70' };
    default:
      return { bg: 'bg-gray-100', border: 'border-l-gray-500', text: 'text-gray-900' };
  }
}

// ============================================================================
// BOOKING BLOCK COMPONENT
// ============================================================================

interface BookingBlockProps {
  booking: Booking;
  startHour: number;
  pixelsPerHour: number;
  operatingStartHour: number;
  rowHeight: number;
  onClick?: () => void;
}

const BookingBlock: React.FC<BookingBlockProps> = ({
  booking,
  startHour,
  pixelsPerHour,
  operatingStartHour,
  rowHeight,
  onClick,
}) => {
  let bookingStart = parseTimeToHours(booking.start_time);
  let bookingEnd = parseTimeToHours(booking.end_time);

  // Handle overnight: if booking time is before operating start, it's past midnight
  if (bookingStart < operatingStartHour) bookingStart += 24;
  if (bookingEnd <= bookingStart) bookingEnd += 24;

  const duration = bookingEnd - bookingStart;

  const left = (bookingStart - operatingStartHour) * pixelsPerHour;
  const width = duration * pixelsPerHour;

  const colors = getBookingColors(booking.type);

  return (
    <div
      className={`absolute top-1 bottom-1 ${colors.bg} ${colors.border} border-l-[3px] rounded-md px-2 py-1 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 overflow-hidden`}
      style={{
        left: `${left}px`,
        width: `${width - 4}px`,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      <div className={`text-[10px] font-bold ${colors.text}`}>
        {booking.start_time} - {booking.end_time}
      </div>
      <div className={`text-xs font-semibold ${colors.text} truncate`}>
        {booking.customer_name || booking.customer || 'N/A'}
      </div>
      {booking.sport_name && (
        <div className={`text-[10px] ${colors.text} truncate opacity-80`}>
          {booking.sport_name}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// BOOKING DETAILS MODAL
// ============================================================================

interface BookingModalProps {
  booking: Booking | null;
  onClose: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({ booking, onClose }) => {
  if (!booking) return null;

  const colors = getBookingColors(booking.type);
  const isExternal = booking.type === 'external';
  const isBlocked = booking.type === 'blocked';
  const isBlockType = isExternal || isBlocked;

  // Get partner/block info from rawData if available
  const rawData = booking.rawData || {};
  const partnerName = rawData.partner_name || 'Third-Party Platform';
  const blockReason = rawData.reason || rawData.block_reason;
  const blockReference = rawData.block_reference;

  // Determine modal title
  const getTitle = () => {
    if (isExternal) return 'External Reservation';
    if (isBlocked) return 'Blocked Time Slot';
    return 'Booking Details';
  };

  // Get type display label
  const getTypeLabel = () => {
    if (isExternal) return 'External';
    if (isBlocked) return 'Blocked';
    return booking.type;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{getTitle()}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="space-y-3">
          {/* External reservation info */}
          {isExternal && (
            <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
              <p className="text-sm font-semibold text-teal-800">
                🔗 Booked via {partnerName}
              </p>
              <p className="text-xs text-teal-600 mt-1">
                This slot was reserved through a third-party platform.
              </p>
            </div>
          )}

          {/* Blocked slot info */}
          {isBlocked && !isExternal && (
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm font-semibold text-orange-800">
                🔒 {blockReason ? blockReason.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'Blocked'}
              </p>
            </div>
          )}

          {/* Customer - only show for regular bookings */}
          {!isBlockType && (
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-semibold text-gray-600">Customer</span>
              <span className="text-sm font-semibold text-gray-900">
                {booking.customer_name || booking.customer || 'N/A'}
              </span>
            </div>
          )}

          {booking.sport_name && (
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-semibold text-gray-600">Sport</span>
              <span className="text-sm font-semibold text-gray-900">{booking.sport_name}</span>
            </div>
          )}

          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-semibold text-gray-600">Time</span>
            <span className="text-sm font-semibold text-gray-900">
              {booking.start_time} - {booking.end_time}
            </span>
          </div>

          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-semibold text-gray-600">Type</span>
            <span className={`text-sm font-semibold capitalize ${colors.text}`}>
              {getTypeLabel()}
            </span>
          </div>

          {/* Reference - show block_reference for external, booking_reference for others */}
          {(booking.booking_reference || blockReference) && (
            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-semibold text-gray-600">Reference</span>
              <span className="text-sm font-semibold text-gray-900 font-mono">
                {blockReference || booking.booking_reference}
              </span>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-gray-200 text-gray-900 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DayViewTimeline: React.FC<DayViewTimelineProps> = ({
  selectedDate,
  courts,
  operatingHours,
  bookings,
  onBookingClick,
  onEmptySlotClick,
  isLoading = false,
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  // Get operating hours for selected day
  const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();
  const todayHours = operatingHours?.find((h) => h.day_of_week.toLowerCase() === dayOfWeek);

  const operatingStartHour = todayHours?.opening_time
    ? parseTimeToHours(todayHours.opening_time)
    : 6;
  let operatingEndHour = todayHours?.closing_time
    ? parseTimeToHours(todayHours.closing_time)
    : 23;

  // Handle overnight hours (e.g., 15:00-00:00 or 18:00-02:00)
  if (operatingEndHour <= operatingStartHour) {
    operatingEndHour += 24;
  }

  const isClosed = todayHours?.is_closed ?? false;

  // Configuration
  const pixelsPerHour = 100;
  const rowHeight = 70;
  const subCourtRowHeight = 55;
  const courtsColumnWidth = 200;

  // Group courts by parent-child relationship
  const courtGroups = useMemo(() => {
    const groups: { parent: Court | null; children: Court[] }[] = [];
    const parentIds = new Set<number>();
    const childMap = new Map<number, Court[]>();

    // First pass: identify parents and children
    courts.forEach((court) => {
      if (court.parent_court_id) {
        if (!childMap.has(court.parent_court_id)) {
          childMap.set(court.parent_court_id, []);
        }
        childMap.get(court.parent_court_id)!.push(court);
      } else {
        const children = courts.filter((c) => c.parent_court_id === court.id);
        if (children.length > 0) {
          parentIds.add(court.id);
        }
      }
    });

    // Second pass: create groups
    courts.forEach((court) => {
      if (court.parent_court_id) {
        // Skip - will be added as child
        return;
      }

      const children = childMap.get(court.id) || [];
      if (children.length > 0) {
        // This is a parent with children - show children only (they represent the actual bookable units)
        groups.push({ parent: court, children: children.sort((a, b) => a.id - b.id) });
      } else {
        // Standalone court
        groups.push({ parent: null, children: [court] });
      }
    });

    return groups;
  }, [courts]);

  // All court rows for rendering (flattened)
  const courtRows = useMemo(() => {
    const rows: { court: Court; isChild: boolean; parentName?: string; isParent?: boolean }[] = [];

    courtGroups.forEach((group) => {
      if (group.parent) {
        // Parent-child group - show parent first, then children
        rows.push({ court: group.parent, isChild: false, isParent: true });
        group.children.forEach((child) => {
          rows.push({ court: child, isChild: true, parentName: group.parent!.name });
        });
      } else {
        // Standalone
        group.children.forEach((court) => {
          rows.push({ court, isChild: false });
        });
      }
    });

    return rows;
  }, [courtGroups]);

  // Generate time headers
  const timeHeaders = useMemo(() => {
    const headers: number[] = [];
    for (let hour = Math.floor(operatingStartHour); hour <= Math.ceil(operatingEndHour); hour++) {
      headers.push(hour);
    }
    return headers;
  }, [operatingStartHour, operatingEndHour]);

  // Calculate timeline width
  const timelineWidth = timeHeaders.length * pixelsPerHour;

  // Scroll to current time on mount
  useEffect(() => {
    if (timelineRef.current && !isClosed) {
      const now = new Date();
      let currentHour = now.getHours() + now.getMinutes() / 60;

      // Handle overnight: if current time is past midnight but venue is still open
      if (currentHour < operatingStartHour && operatingEndHour > 24) {
        currentHour += 24;
      }

      if (currentHour >= operatingStartHour && currentHour <= operatingEndHour) {
        const scrollPosition = (currentHour - operatingStartHour) * pixelsPerHour - 200;
        timelineRef.current.scrollLeft = Math.max(0, scrollPosition);
      }
    }
  }, [operatingStartHour, operatingEndHour, isClosed]);

  // Current time indicator position
  const currentTimePosition = useMemo(() => {
    const now = new Date();
    let currentHour = now.getHours() + now.getMinutes() / 60;

    // Handle overnight: if current time is past midnight but venue is still open
    if (currentHour < operatingStartHour && operatingEndHour > 24) {
      currentHour += 24;
    }

    if (currentHour >= operatingStartHour && currentHour <= operatingEndHour) {
      return (currentHour - operatingStartHour) * pixelsPerHour;
    }
    return null;
  }, [operatingStartHour, operatingEndHour]);

  // Handle booking click
  const handleBookingClick = (booking: Booking) => {
    if (onBookingClick) {
      onBookingClick(booking);
    } else {
      setSelectedBooking(booking);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isClosed) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Venue Closed</h3>
        <p className="text-gray-500">The venue is closed on {format(selectedDate, 'EEEE')}s</p>
      </div>
    );
  }

  if (courts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">No Courts Available</h3>
        <p className="text-gray-500">Please add courts/fields to your venue first</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex">
        {/* Courts Column (Left Side) */}
        <div
          className="flex-shrink-0 border-r-2 border-gray-200 bg-gray-50"
          style={{ width: courtsColumnWidth }}
        >
          {/* Header */}
          <div
            className="h-[50px] flex items-center justify-center border-b-2 border-gray-200 bg-white"
          >
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
              Courts / Fields
            </span>
          </div>

          {/* Court Rows */}
          {courtRows.map((row, idx) => (
            <div
              key={row.court.id}
              className={`border-b border-gray-200 flex flex-col justify-center px-4 transition-colors hover:bg-white ${
                row.isChild ? 'bg-white/50' : row.isParent ? 'bg-blue-50/30' : ''
              }`}
              style={{ height: row.isChild ? subCourtRowHeight : rowHeight }}
            >
              {row.isChild && row.parentName ? (
                <>
                  <div className="text-[11px] text-gray-400 font-medium">{row.parentName}</div>
                  <div className="text-sm font-semibold text-gray-900">{row.court.name}</div>
                </>
              ) : row.isParent ? (
                <>
                  <div className="text-sm font-bold text-gray-900">{row.court.name}</div>
                  <div className="text-xs text-blue-600 font-medium">Full Court</div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-gray-900">{row.court.name}</div>
                  <div className="text-xs text-gray-500">{row.court.sport?.name || 'Sport'}</div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Timeline Column (Right Side - Scrollable) */}
        <div className="flex-1 overflow-x-auto" ref={timelineRef}>
          {/* Time Headers */}
          <div
            className="h-[50px] flex border-b-2 border-gray-200 bg-white sticky top-0 z-10"
            style={{ width: timelineWidth }}
          >
            {timeHeaders.map((hour) => (
              <div
                key={hour}
                className="flex items-center justify-center border-r border-gray-200 text-xs font-semibold text-gray-600"
                style={{ width: pixelsPerHour }}
              >
                {(hour >= 24 ? hour - 24 : hour).toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Timeline Grid */}
          <div className="relative" style={{ width: timelineWidth }}>
            {/* Current Time Indicator */}
            {currentTimePosition !== null && (
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
                style={{ left: currentTimePosition }}
              >
                <div className="absolute -top-1 -left-1.5 w-4 h-4 bg-red-500 rounded-full shadow-md" />
              </div>
            )}

            {/* Court Timeline Rows */}
            {courtRows.map((row) => {
              const courtBookings = bookings.filter((b) => Number(b.court_id) === Number(row.court.id));
              const height = row.isChild ? subCourtRowHeight : rowHeight;

              return (
                <div
                  key={row.court.id}
                  className={`relative border-b border-gray-200 ${row.isParent ? 'bg-blue-50/20' : ''}`}
                  style={{ height }}
                >
                  {/* Time Cell Backgrounds */}
                  <div className="absolute inset-0 flex">
                    {timeHeaders.map((hour) => (
                      <div
                        key={hour}
                        className="border-r border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                        style={{ width: pixelsPerHour }}
                        onClick={() => onEmptySlotClick?.(row.court.id, `${hour >= 24 ? hour - 24 : hour}:00`)}
                      />
                    ))}
                  </div>

                  {/* Bookings */}
                  {courtBookings.map((booking) => (
                    <BookingBlock
                      key={booking.id}
                      booking={booking}
                      startHour={operatingStartHour}
                      pixelsPerHour={pixelsPerHour}
                      operatingStartHour={operatingStartHour}
                      rowHeight={height}
                      onClick={() => handleBookingClick(booking)}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Internal Booking Modal (if no external handler provided) */}
      {!onBookingClick && (
        <BookingModal booking={selectedBooking} onClose={() => setSelectedBooking(null)} />
      )}
    </div>
  );
};

export default DayViewTimeline;
